import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Autocomplete, Drawer, InputAdornment, MenuItem, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { selectRealUser } from "../../../features/userSlice";
import { startImpersonation } from "../../../utils/impersonation";
import { requestSessionRefresh } from "../../../utils/liveSession";
import { normalizeDate } from "../../../utilities";
import {
  createCustomRole,
  fetchAccessAudit,
  fetchSession,
  updateAccessUser,
  updatePageGrant,
} from "./accessApi";
import { Notice, RoleChip, UserAvatar, isOnline, timeAgo } from "./accessUi";
import AuditList from "./AuditList";

const DIRECTORY = "__directory__";

// One user: roles and branch are edited together and saved with one click
// (one notification to them, one history entry); page grants and account
// status save straight away. Everything reaches the person within ~20
// seconds without them signing out (utils/liveSession.js).
const UserDrawer = ({
  user,
  roleCatalog,
  branchOptions,
  pages,
  grantsByKey,
  actorId,
  onUserUpdated,
  onGrantChange,
  onRoleCreated,
  onClose,
}) => {
  const realUser = useSelector(selectRealUser);
  const targetId = user.id || user._id;
  const isSelf = Boolean(user.id) && user.id === actorId;
  const currentRoles = useMemo(() => user.roles || [], [user.roles]);
  const currentBranch = user.branch_override ? user.branch_override : DIRECTORY;

  const [roles, setRoles] = useState(currentRoles);
  const [branch, setBranch] = useState(currentBranch);
  const [roleQuery, setRoleQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [pageInput, setPageInput] = useState("");
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    if (!actorId) return;
    let cancelled = false;
    fetchAccessAudit(actorId, targetId)
      .then((res) => !cancelled && setAudit(res.audit))
      .catch(() => !cancelled && setAudit([]));
    return () => {
      cancelled = true;
    };
  }, [actorId, targetId, user]);

  const addRoles = roles.filter((r) => !currentRoles.includes(r));
  const removeRoles = currentRoles.filter((r) => !roles.includes(r));
  const branchDirty = branch !== currentBranch;
  const dirty = addRoles.length > 0 || removeRoles.length > 0 || branchDirty;

  const toggleRole = (name) => {
    if (isSelf && name === "Admin" && roles.includes("Admin")) {
      setMessage({ type: "error", text: "You can't remove your own Admin role." });
      return;
    }
    setRoles((prev) => (prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name]));
  };

  // Typing a role that doesn't exist offers to create it (a custom role, same
  // as the Roles tab on Page Access) and tick it for this person.
  const newRoleName = roleQuery.trim().replace(/\s+/g, " ");
  const canCreateRole =
    newRoleName.length >= 2 &&
    !roleCatalog.some((r) => r.name.toLowerCase() === newRoleName.toLowerCase());

  const createRole = () =>
    run(async () => {
      const { role } = await createCustomRole(actorId, newRoleName, "");
      onRoleCreated(role);
      setRoles((prev) => [...new Set([...prev, role.name])]);
      setRoleQuery("");
    }, `Created ${newRoleName} and ticked it — save to give it to ${user.name?.split(" ")[0] || "them"}.`);

  const filteredRoles = useMemo(() => {
    const q = roleQuery.trim().toLowerCase();
    return q
      ? roleCatalog.filter((r) => [r.name, r.description].some((f) => f.toLowerCase().includes(q)))
      : roleCatalog;
  }, [roleCatalog, roleQuery]);

  // Page access as it stands after the SAVED roles (not the unsaved edit).
  const access = useMemo(() => {
    const viaRole = [];
    const direct = [];
    pages.forEach((page) => {
      const grant = grantsByKey.get(page.key);
      if (user.id && grant?.users.includes(user.id)) direct.push(page);
      else if ([...page.roles, ...(grant?.roles || [])].some((r) => currentRoles.includes(r))) {
        viaRole.push(page);
      }
    });
    return { viaRole, direct };
  }, [pages, grantsByKey, user.id, currentRoles]);

  const pageOptions = useMemo(
    () => pages.filter((p) => !access.direct.includes(p) && !access.viaRole.includes(p)),
    [pages, access]
  );

  const run = async (fn, success) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      setMessage({ type: "success", text: success });
      if (isSelf) requestSessionRefresh();
      return true;
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Couldn't save that change." });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveChanges = () =>
    run(async () => {
      const { user: next } = await updateAccessUser(actorId, targetId, {
        addRoles,
        removeRoles,
        ...(branchDirty ? { branch: branch === DIRECTORY ? null : branch } : {}),
      });
      onUserUpdated(next);
      setRoles(next.roles || []);
      setBranch(next.branch_override || DIRECTORY);
    }, isSelf ? "Saved. Your menus update now." : "Saved. They'll see it within about 20 seconds and get a notification.");

  const discard = () => {
    setRoles(currentRoles);
    setBranch(currentBranch);
    setMessage(null);
  };

  const setStatus = (deactivated) =>
    run(async () => {
      const { user: next } = await updateAccessUser(actorId, targetId, { deactivated });
      onUserUpdated(next);
      setConfirmDeactivate(false);
    }, deactivated ? "Account deactivated. Any open Node tabs sign out within about 20 seconds." : "Account reactivated.");

  const changeGrant = (page, change, success) =>
    run(async () => {
      const { grant } = await updatePageGrant(actorId, {
        key: page.key,
        name: page.name,
        redirect: page.redirect,
        ...change,
      });
      onGrantChange(grant);
    }, success);

  const handleClose = () => {
    if (dirty && !window.confirm("Discard your unsaved changes?")) return;
    onClose();
  };

  const canPreview = realUser?.roles?.includes("Admin") && user.id && user.id !== realUser.id;
  const handlePreview = async () => {
    // Their page grants too, so the preview opens exactly what they can.
    let grantedPaths = [];
    try {
      grantedPaths = (await fetchSession(user.id)).grantedPaths || [];
    } catch (err) {
      // Preview with roles only.
    }
    startImpersonation({
      user: {
        name: user.name,
        email: user.email,
        id: user.id,
        title: user.title,
        roles: user.roles || [],
        grantedPaths,
        branch: user.branch,
      },
      startedBy: { id: realUser.id, name: realUser.name },
      startedAt: new Date().toISOString(),
    });
    window.location.assign("/");
  };

  const online = isOnline(user);
  const firstName = user.name?.split(" ")[0] || "this user";

  return (
    <Drawer anchor="right" open onClose={handleClose} PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}>
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="app-divider flex items-start gap-3 border-b px-5 py-4">
          <span className="relative">
            <UserAvatar user={user} size="lg" />
            {online && (
              <span className="absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-500 ring-2 ring-[color:var(--app-surface-bg)]" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-2 truncate text-lg font-semibold tracking-tight">
              <span className="truncate">{user.name || user.email}</span>
              {user.deactivated && (
                <span className="shrink-0 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:text-rose-300">
                  Deactivated
                </span>
              )}
            </h2>
            <p className="app-muted truncate text-sm">{user.title || "No title"}</p>
            <p className="app-muted truncate text-xs">
              {online ? "Online now" : `Last seen ${timeAgo(user.last_seen).toLowerCase()}`}
              {user.email ? ` · ${user.email}` : ""}
            </p>
          </div>
          <button type="button" onClick={handleClose} aria-label="Close" className="app-icon-btn -mr-2 shrink-0">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          <Notice message={message} />

          {!user.id && (
            <Notice
              message={{
                type: "info",
                text: "This person hasn't signed in to Node yet. You can set roles and a branch now; page grants and preview need them to sign in first.",
              }}
            />
          )}

          {/* Branch */}
          <section>
            <p className="app-eyebrow mb-2">Branch</p>
            <TextField
              select
              size="small"
              fullWidth
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              disabled={busy}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PlaceRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value={DIRECTORY}>
                From the directory{user.directory_branch ? ` (${user.directory_branch})` : user.branch && !user.branch_override ? ` (${user.branch})` : ""}
              </MenuItem>
              {branchOptions.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </TextField>
            <p className="app-muted mt-2 text-xs">
              {user.branch_override
                ? "Set here, so it stays put when they sign in. Pick “From the directory” to go back to the directory's branch."
                : "Comes from the directory every time they sign in. Picking a branch here overrides it."}
            </p>
          </section>

          {/* Roles */}
          <section className="app-divider border-t pt-5">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="app-eyebrow">Roles</p>
              <span className="app-muted text-xs tabular-nums">{roles.length} selected</span>
            </div>
            <div className="mb-3 flex min-h-[1.75rem] flex-wrap gap-1.5">
              {roles.length ? (
                roles.map((r) => (
                  <RoleChip
                    key={r}
                    role={r}
                    tone={roleCatalog.find((c) => c.name === r)?.custom ? "custom" : "extra"}
                    onRemove={() => toggleRole(r)}
                    disabled={busy}
                  />
                ))
              ) : (
                <p className="app-muted text-sm">No roles — they only see pages open to everyone.</p>
              )}
            </div>
            <TextField
              size="small"
              fullWidth
              placeholder="Find or create a role…"
              value={roleQuery}
              onChange={(e) => setRoleQuery(e.target.value)}
              inputProps={{ "aria-label": "Find a role" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <div className="app-surface mt-2 max-h-64 overflow-y-auto rounded-xl border border-[color:var(--app-surface-border)] p-1">
              {filteredRoles.map((r) => {
                const checked = roles.includes(r.name);
                return (
                  <button
                    key={r.name}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    onClick={() => toggleRole(r.name)}
                    disabled={busy}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm transition hover:bg-[color:var(--app-hover)] disabled:opacity-50"
                  >
                    <span
                      className={`flex h-[1.125rem] w-[1.125rem] shrink-0 items-center justify-center rounded border ${
                        checked ? "border-sky-600 bg-sky-600 text-white" : "border-[color:var(--app-input-border)]"
                      }`}
                    >
                      {checked && <CheckRoundedIcon sx={{ fontSize: 14 }} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{r.name}</span>
                    {r.custom && (
                      <span className="shrink-0 rounded-full bg-violet-500/15 px-1.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
                        Custom
                      </span>
                    )}
                  </button>
                );
              })}
              {filteredRoles.length === 0 && !canCreateRole && (
                <p className="app-muted px-2.5 py-2 text-sm">No roles match.</p>
              )}
              {canCreateRole && (
                <button
                  type="button"
                  onClick={createRole}
                  disabled={busy}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm font-medium text-violet-700 transition hover:bg-violet-500/10 disabled:opacity-50 dark:text-violet-300"
                >
                  <AddRoundedIcon sx={{ fontSize: 18 }} />
                  Create “{newRoleName}” as a new role
                </button>
              )}
            </div>
            <p className="app-muted mt-2 text-xs">
              New roles open nothing on their own — add them to pages on Page Access.
            </p>
          </section>

          {/* Page access */}
          <section className="app-divider border-t pt-5">
            <p className="app-eyebrow mb-1">Page access</p>
            <p className="app-muted mb-3 text-xs">
              Opens {access.viaRole.length + access.direct.length} role-gated{" "}
              {access.viaRole.length + access.direct.length === 1 ? "page" : "pages"}
              {dirty ? " (before your unsaved role changes)" : ""}. Direct access saves right away.
            </p>
            {access.direct.length > 0 && (
              <ul className="mb-3 space-y-1">
                {access.direct.map((page) => (
                  <li key={page.key} className="flex items-center gap-2 rounded-xl bg-sky-500/10 px-3 py-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{page.name}</p>
                      <p className="app-muted truncate text-xs">{page.section} · direct access</p>
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        changeGrant(page, { removeUsers: [user.id] }, `Took away direct access to ${page.name}.`)
                      }
                      className="app-btn app-btn-sm app-btn-ghost text-rose-700 dark:text-rose-300"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {user.id && (
              <Autocomplete
                size="small"
                options={pageOptions}
                groupBy={(p) => p.section}
                getOptionLabel={(p) => p.name}
                value={null}
                inputValue={pageInput}
                onInputChange={(e, value) => setPageInput(value)}
                onChange={(e, page) => {
                  if (!page) return;
                  setPageInput("");
                  changeGrant(
                    page,
                    { addUsers: [user.id] },
                    `${firstName} can now open ${page.name}. They'll get a notification.`
                  );
                }}
                disabled={busy}
                blurOnSelect
                renderInput={(params) => <TextField {...params} placeholder="Give access to one more page…" />}
                noOptionsText="They can already open every gated page"
              />
            )}
            {access.viaRole.length > 0 && (
              <details className="mt-3">
                <summary className="app-link cursor-pointer text-xs">
                  {access.viaRole.length} {access.viaRole.length === 1 ? "page" : "pages"} through their roles
                </summary>
                <p className="app-muted mt-2 text-xs leading-relaxed">
                  {access.viaRole.map((p) => p.name).join(", ")}
                </p>
              </details>
            )}
          </section>

          {/* Preview */}
          {canPreview && (
            <section className="app-divider border-t pt-5">
              <p className="app-eyebrow mb-2">Preview</p>
              <button
                type="button"
                onClick={handlePreview}
                disabled={busy || dirty}
                className="app-btn app-btn-sm border-sky-500/50 text-sky-700 hover:bg-sky-500/10 dark:text-sky-300"
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
                View Node as {firstName}
              </button>
              <p className="app-muted mt-2 text-xs">
                {dirty
                  ? "Save or discard your changes first."
                  : "Browse with their roles, branch, and page access. A banner stays on screen until you exit the preview."}
              </p>
            </section>
          )}

          {/* Account */}
          <section className="app-divider border-t pt-5">
            <p className="app-eyebrow mb-2">Account</p>
            <dl className="mb-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
              <dt className="app-muted">Last page</dt>
              <dd className="truncate">{user.last_route || "—"}</dd>
              <dt className="app-muted">Time in Node</dt>
              <dd className="tabular-nums">
                {user.time_spent ? `${Math.round((user.time_spent / 3600) * 10) / 10} hrs` : "—"}
              </dd>
              <dt className="app-muted">Two-step sign-in</dt>
              <dd>{user.mfaEnabled ? "On" : "Off"}</dd>
              <dt className="app-muted">Microsoft 365</dt>
              <dd>
                {!user.directory_sync
                  ? "Not checked yet"
                  : !user.directory_sync.inDirectory
                  ? "Not found"
                  : [
                      user.directory_sync.accountEnabled ? "Active" : "Disabled",
                      user.directory_sync.hasMailbox ? "mailbox" : "no mailbox",
                      user.directory_sync.hasMailbox && !user.directory_sync.listed ? "hidden from address book" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                {user.directory_sync?.syncedAt && (
                  <span className="app-muted"> ({timeAgo(user.directory_sync.syncedAt).toLowerCase()})</span>
                )}
              </dd>
              {user.auto_roles_at && (
                <>
                  <dt className="app-muted">Roles</dt>
                  <dd>Given automatically {timeAgo(user.auto_roles_at).toLowerCase()}</dd>
                </>
              )}
            </dl>
            {isSelf ? (
              <p className="app-muted text-xs">You can't deactivate your own account.</p>
            ) : user.deactivated ? (
              <div>
                <p className="mb-2 text-sm">
                  Deactivated{user.deactivated_at ? ` ${normalizeDate(user.deactivated_at)}` : ""}
                  {user.deactivated_source === "directory"
                    ? ` by the directory sync (${user.deactivated_reason || "gone from Microsoft 365"})`
                    : ""}
                  . They can't sign in.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus(false)}
                  disabled={busy}
                  className="app-btn app-btn-sm border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300"
                >
                  Reactivate account
                </button>
              </div>
            ) : confirmDeactivate ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm">
                  Deactivate <strong>{user.name}</strong>? They're signed out and can't sign back in.
                </span>
                <button
                  type="button"
                  onClick={() => setStatus(true)}
                  disabled={busy}
                  className="app-btn app-btn-sm app-btn-danger"
                >
                  Yes, deactivate
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeactivate(false)}
                  disabled={busy}
                  className="app-btn app-btn-sm app-btn-ghost"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDeactivate(true)}
                disabled={busy}
                className="app-btn app-btn-sm app-btn-ghost text-rose-700 dark:text-rose-300"
              >
                Deactivate account
              </button>
            )}
          </section>

          {/* History */}
          <section className="app-divider border-t pt-5">
            <p className="app-eyebrow mb-3">History</p>
            {audit === null ? (
              <p className="app-muted text-sm">Loading…</p>
            ) : (
              <AuditList compact entries={audit.slice(0, 15)} emptyText="No changes recorded yet." />
            )}
          </section>
        </div>

        {/* Save bar */}
        {dirty && (
          <div className="app-divider flex flex-wrap items-center justify-between gap-2 border-t bg-[color:var(--app-surface-bg)] px-5 py-3">
            <p className="text-sm">
              {[
                addRoles.length && `+${addRoles.length} role${addRoles.length === 1 ? "" : "s"}`,
                removeRoles.length && `−${removeRoles.length} role${removeRoles.length === 1 ? "" : "s"}`,
                branchDirty && "branch",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={discard} disabled={busy} className="app-btn app-btn-sm app-btn-ghost">
                Discard
              </button>
              <button type="button" onClick={saveChanges} disabled={busy} className="app-btn app-btn-sm app-btn-primary">
                Save changes
              </button>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default UserDrawer;
